(ns app.domain.label.model
  (:require #?(:cljs [core.front.feature-flags :as ff])
            [app.domain.group.model :as group-model]
            [app.domain.util :as du]
            ;; Needed so that the workspace group-model/get-authority-group multimethod 
            ;; implementation is loaded when label model code follows :entity/parent.
            [app.domain.workspace.model]
            [clojure.string :as str]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]))


(def max-import-file-size
  "Maximum allowed import file size in bytes (1MB)."
  (* 1024 1024))

(def is-label-group?
  (partial eu/is-type? :entity.type/label-group))

(def is-label?
  (partial eu/is-type? :entity.type/label))


(def label-group-states
  [:label-group.state/active
   :label-group.state/assigned
   :label-group.state/removed])

(def label-states
  [:label.state/active
   :label.state/removed])

(defmethod du/is-active? :entity.type/label-group
  [{state :domain/state}]
  (not= state :label-group.state/removed))

(defmethod du/is-active? :entity.type/label
  [{state :domain/state}]
  (not= state :label.state/removed))

(defmethod du/is-active? :entity.type/inspection-deficiency-label
  [{state :domain/state}]
  (not= state :inspection.deficiency-label.state/removed))

(defmethod group-model/get-authority-group :entity.type/label-group
  [label-group]
  (or (:domain/authority-group label-group)
      (some-> label-group :entity/parent group-model/get-authority-group)))

(defmethod group-model/get-authority-group :entity.type/label
  [label]
  (-> label :entity/parent group-model/get-authority-group))

(def ^{:arglists '([workspace])} workspace-label-groups
  "Get label groups that belong to a workspace (not authority group). A workspace owns exactly its
   child label groups, so `:entity/parent` is the whole story of ownership."
  (child/child-entities-of-type-fn :entity.type/label-group))

(defn active-workspace-label-groups
  "Get every label group the workspace owns that has not been deleted — both the ones it uses
   (:label-group.state/assigned) and the ones it merely owns (:label-group.state/active). Creating or
   importing a group puts it here; see `active-assigned-label-groups` for the ones in use."
  [workspace]
  (->> (workspace-label-groups workspace)
       (filter du/is-active?)))

(defn active-assigned-label-groups
  "Get the label groups in use on a workspace, i.e. those put into use via
   :process.signal/assign-workspace-label-groups. Always a subset of `active-workspace-label-groups`:
   a group the workspace owns but does not use stays :label-group.state/active. Authority label
   groups are templates and are copied onto the workspace when assigned, so they never appear here."
  [workspace]
  (->> (workspace-label-groups workspace)
       (filter (du/in-state-fn :label-group.state/assigned))))

(defmethod group-model/get-authority-group :entity.type/inspection-deficiency-label
  [deficiency-label]
  (-> deficiency-label :entity/parent group-model/get-authority-group))

(def ^{:arglists '([label-group])} label-group-labels
  (child/child-entities-of-type-fn :entity.type/label))

(defn active-labels [label-group]
  (->> (label-group-labels label-group)
       (filter du/is-active?)))

(def ^{:arglists '([deficiency])} deficiency-labels
  (child/child-entities-of-type-fn :entity.type/inspection-deficiency-label))

(defn deficiency-label-active? [deficiency-label]
  (let [label (:inspection.deficiency-label/label deficiency-label)]
    (and (du/is-active? deficiency-label)
         (is-label? label)
         (du/is-active? label))))

(defn active-deficiency-labels [deficiency]
  (->> (deficiency-labels deficiency)
       (filter deficiency-label-active?)))

(defn deficiency-label-cleared? [deficiency-label]
  (= (:domain/state deficiency-label) :inspection.deficiency-label.state/cleared))

(defn deficiency-has-label? [deficiency label]
  (->> (active-deficiency-labels deficiency)
       (some #(= (:db/id label) (-> % :inspection.deficiency-label/label :db/id)))))

(def ^{:arglists '([authority-group])} authority-group-label-groups
  (group-model/entities-of-group-fn :entity.type/label-group))

(defn active-label-groups [authority-group]
  (->> (authority-group-label-groups authority-group)
       (filter du/is-active?)))

(defn active-label-groups-in-scope
  "Active label groups for whichever scope is provided — authority group or workspace."
  [authority-group workspace]
  (if authority-group
    (active-label-groups authority-group)
    (active-workspace-label-groups workspace)))

(defn label-group-name [label-group]
  (:label-group/name label-group))

(defn label-name [label]
  (:label/name label))

(defn normalized-label-name [name]
  (some-> name str/trim str/lower-case))

(defn label-name-taken?
  "Returns true if candidate name already exists in labels (case-insensitive, trimmed).
   Accepts either a seq of label entities."
  [labels-or-name-set candidate-name]
  (let [candidate (normalized-label-name candidate-name)]
    (and (seq candidate)
         (if (set? labels-or-name-set)
           (contains? labels-or-name-set candidate)
           (->> labels-or-name-set
                (map label-name)
                (some #(= candidate (normalized-label-name %))))))))

(defn conflict-new-name-valid?
  "Check if a new group/label name is valid for use.
   Returns true if the name is non-blank and not already present in `existing-names`
   (case-insensitive, trimmed). `existing-names` may be a map keyed by name, or a
   set/seq of names."
  [new-name existing-names]
  (let [trimmed   (str/trim (or new-name ""))
        candidate (normalized-label-name new-name)
        names     (if (map? existing-names) (keys existing-names) existing-names)]
    (boolean
      (and (seq trimmed)
           (not (some #(= candidate (normalized-label-name %)) names))))))

(defn labels-enabled?
  "Check if labels feature is enabled for a group.
   Respects the mobile app safety condition."
  [group feature-flags]
  (and (group-model/has-feature? group :group.feature/inspection-labels)
       (group-model/has-mobile-feature?
         [(-> feature-flags :feature-flag.id/inspection-labels)
          (-> feature-flags :feature-flag.id/inspection-labels-mobile)
          (group-model/has-feature? group :group.feature/mobile-app)])))

#?(:cljs
   (defn labels-enabled-for-group?
     "Check if labels feature is enabled for a group.
      Frontend-only convenience function that fetches feature flags automatically."
     [group]
     (labels-enabled? group
                      {:feature-flag.id/inspection-labels        (ff/enabled? :feature-flag.id/inspection-labels)
                       :feature-flag.id/inspection-labels-mobile (ff/enabled? :feature-flag.id/inspection-labels-mobile)})))
